import { v4 as uuid } from "uuid"
import getUpdatedMatrix from "reducers/lockingSystemReducer/utils/getUpdatedMatrix"
import getLockingSystemPricePortions, {
	getLockingSystemMsrpPricePortions,
} from "reducers/lockingSystemReducer/utils/getLockingSystemPricePortions"

import {
	DispatchLockingSystemReducer,
	LockingSystemReducerState,
} from "reducers/lockingSystemReducer"
import { LockingSystemComponent } from "@secureo/common/typings/LockingSystem"

type AddKeyComponentPayload = Pick<
	LockingSystemReducerState,
	| "keyComponentsUuids"
	| "componentsByUuid"
	| "matrix"
	| "matrixType"

	// Price
	| "singleNetPriceCents"
	| "singleGrossPriceCents"
	| "totalNetPriceCents"
	| "totalGrossPriceCents"

	// Msrp price
	| "singleNetMsrpPriceCents"
	| "singleGrossMsrpPriceCents"
	| "totalNetMsrpPriceCents"
	| "totalGrossMsrpPriceCents"
>

export const _getAddDefaultKeyComponentPayload = (
	reducerState: LockingSystemReducerState,
	count: number,
) => {
	const { defaultKeyComponent, lockingComponentsUuids, quantity } = reducerState

	const newKeyComponents: LockingSystemComponent[] = []

	for (let i = 0; i < count; i++) {
		const keyComponent: LockingSystemComponent = {
			...defaultKeyComponent,
			uuid: uuid(),
		}

		newKeyComponents.push(keyComponent)
	}

	const componentsByUuid = newKeyComponents.reduce((componentsByUuid, keyComponent) => {
		return {
			...componentsByUuid,
			[keyComponent.uuid]: keyComponent,
		}
	}, reducerState.componentsByUuid)

	// Recalculate locking system state
	const keyComponentsUuids = [
		...reducerState.keyComponentsUuids,
		...newKeyComponents.map(({ uuid }) => uuid),
	]

	const { matrix, matrixType } = getUpdatedMatrix(
		lockingComponentsUuids,
		keyComponentsUuids,
		reducerState.matrix,
	)

	const { singleNetPriceCents, singleGrossPriceCents, totalNetPriceCents, totalGrossPriceCents } =
		getLockingSystemPricePortions(componentsByUuid, quantity)

	const {
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
	} = getLockingSystemMsrpPricePortions(componentsByUuid, quantity)

	const payload: AddKeyComponentPayload = {
		keyComponentsUuids,
		componentsByUuid,
		matrix,
		matrixType,

		// Price
		singleNetPriceCents,
		singleGrossPriceCents,
		totalNetPriceCents,
		totalGrossPriceCents,

		// Msrp price
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
	}

	return payload
}

export const addDefaultKeyComponent = (
	dispatch: DispatchLockingSystemReducer,
	reducerState: LockingSystemReducerState,
	count = 1,
) => {
	const payload = _getAddDefaultKeyComponentPayload(reducerState, count)

	dispatch({
		type: "ADD_KEY_COMPONENT",
		payload,
	})
}
